import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import {
  dataToForm,
  FORM_SCHEMA,
  formToBody,
  MiscAssetsFormValues,
  UpsertMiscAssetsDTO,
} from './MiscAssetsForm.meta';
import { MiscAssetDTO } from '../../tables/MiscAssets/MiscAssets.meta';
import { useUpsertMiscAssetsMutation } from '../../api/useUpsertMiscAssetsMutation';
import { ADD_ASSET_SUCCESS_MESSAGE, EDIT_ASSET_SUCCESS_MESSAGE } from '../../OtherAssets.meta';
import { OtherAssetsDTO } from '../../api/useOtherAssetsQuery';
import {
  EXCLUDE_OPT,
  getAssetBucketOptions,
} from '../components/AssetBucketSelect/AssetBucketSelect.meta';
import { onManagementSelect as baseOnManagementSelect } from '../components/ManagementStyle/ManagementStyle.meta';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';

const FIELDS_TO_SHOW_ADVANCED = [
  'SaleYear',
  'SaleNetEarnings',
  'IsSoldAtRetirement',
  'IncomeGrowth',
];

interface Params {
  clientId: string;
  asset: MiscAssetDTO | null;
  options: OtherAssetsDTO['Options'];
  onSuccess: () => void;
}

export const useMiscAssetsForm = ({ clientId, asset, options, onSuccess }: Params) => {
  const formBag = useForm<MiscAssetsFormValues>({
    defaultValues: dataToForm({ data: asset, options }),
    resolver: yupResolver(FORM_SCHEMA),
  });

  const { Dob: primaryDob, PartnerDob: spouseDob } = useSelector(selectCurrentClient);

  const primaryYob = primaryDob ? new Date(primaryDob).getFullYear() : null;
  const spouseYob = spouseDob ? new Date(spouseDob).getFullYear() : null;

  const [ownerModel, managementStyleSelection] = formBag.watch([
    'ownerModel',
    'orionManagementStyleId',
  ]);

  const mutationBag = useUpsertMiscAssetsMutation({ clientId });

  const ownerOptions = [
    {
      label: 'People',
      options: options.MiscellaneousAssetOptions.ownerOptions.People.map(item => ({
        value: item.Name,
        label: item.Name,
      })),
    },
    {
      label: 'Trusts',
      options: options.MiscellaneousAssetOptions.ownerOptions.Trusts.map(item => ({
        value: item.Name,
        label: item.Name,
      })),
    },
  ];

  const isPrincipal =
    ownerModel.length === 1 &&
    ownerModel[0].Name === options.MiscellaneousAssetOptions.ownerOptions.People[0].Name;
  const isSpouse =
    ownerModel.length === 1 &&
    ownerModel[0].Name === options.MiscellaneousAssetOptions.ownerOptions.People[1]?.Name;
  const isJoint = ownerModel.length > 1 || ownerModel.some(owner => owner.Name === 'Joint');

  const saleYearOptions = options.MiscellaneousAssetOptions.SaleYearOptions.map(year => {
    const yearNumber = Number(year);

    let rightLabel: string | undefined;

    if (Number.isInteger(yearNumber)) {
      const getAgeLabel = (birthYear: number) => `Age ${yearNumber - birthYear}`;

      if (isJoint && primaryYob) {
        rightLabel = `${getAgeLabel(primaryYob)}${spouseYob ? `, ${getAgeLabel(spouseYob)}` : ''}`;
      } else if (isPrincipal && primaryYob) {
        rightLabel = getAgeLabel(primaryYob);
      } else if (isSpouse && spouseYob) {
        rightLabel = getAgeLabel(spouseYob);
      }
    }

    return {
      value: year,
      label: year,
      rightLabel,
    };
  });

  const defaultShowAdvanced =
    !!asset && FIELDS_TO_SHOW_ADVANCED.some(field => !!asset[field] || asset[field] === 0);

  const baseAssetBucketOptions = [
    ...options.AvailableAssetBuckets.map(({ Title }, index) => ({
      label: Title,
      value: index,
      rightLabel:
        index === options.MiscellaneousAssetOptions.DefaultAssetBucketIndex ? 'Default' : undefined,
    })),
    EXCLUDE_OPT,
  ];

  const onSubmit = formBag.handleSubmit(async (values: MiscAssetsFormValues) => {
    const body: UpsertMiscAssetsDTO = {
      clientId,
      Id: asset?.Id || null,
      ...formToBody(values),
    };

    try {
      await mutationBag.mutateAsync(body);

      toast.success(asset?.Id ? EDIT_ASSET_SUCCESS_MESSAGE : ADD_ASSET_SUCCESS_MESSAGE);

      onSuccess();
    } catch (error) {
      console.error(error);
    }
  });

  const {
    AssetBucketingByManagementStyle: assetBucketingByManagementStyle,
    ManagementStyles,
    DisplayAssetBucketing: displayAssetBucketing,
  } = options;

  const managementStylesOptions = ManagementStyles.map(option => ({
    label: option.name,
    value: option.id,
    defaultBucketIndex: option.defaultBucket,
  }));

  const assetBucketOptions = getAssetBucketOptions({
    assetBucketOptions: baseAssetBucketOptions,
    assetBucketingByManagementStyle,
    managementStyleSelection,
    managementStylesOptions,
  });

  const onManagementSelect = baseOnManagementSelect({
    options: managementStylesOptions,
    setValue: (value: number) => formBag.setValue('customBucketIndex', value),
  });

  return {
    data: {
      formBag,
      mutationBag,
      ownerOptions,
      saleYearOptions,
      defaultShowAdvanced,
      assetBucketOptions,
      managementStylesOptions,
      onManagementSelect,
      assetBucketingByManagementStyle,
      allowAssetBucketingCustomization: options.AllowAssetBucketingCustomization,
      displayAssetBucketing,
    },
    actions: {
      onSubmit,
    },
  };
};
